import { apiPost, friendlyError, type ApiError } from "../../api/client.js";

export interface VmListItem {
  vmid: number | null;
  name: string;
  node: string;
  status: string;
  progress?: string;
  creation_id?: number;
  cpu_cores: number;
  memory_mb: number;
  disk_total_bytes: number;
  cpu_usage_fraction: number;
  memory_usage_bytes: number;
  memory_source?: "guest" | "host";
  memory_total_bytes?: number;
  memory_available_bytes?: number;
  swap_usage_bytes?: number;
  swap_total_bytes?: number;
  memory_includes_cache?: boolean;
  uptime_seconds: number;
  backupEnabled: boolean;
  backupOffered: boolean;
  backupPaused: boolean;
}

export interface GuestInterface {
  name: string;
  mac?: string;
  ipv4?: string;
  ipv6?: string;
}

export interface VmDisk {
  slot: string;
  size_bytes: number;
  format: string | null;
  media: string | null;
}

/** Web mirror of the backend's isDataDisk predicate (Phase 26): rows
 * the customer can act on are non-cdrom and not a UEFI efidisk. */
export function isVmDataDisk(d: Pick<VmDisk, "slot"> & { media?: string | null }): boolean {
  return d.media !== "cdrom" && !d.slot.startsWith("efidisk");
}

export interface VmDetail {
  vmid: number;
  name: string;
  node: string;
  status: string;
  cpu_cores: number;
  sockets: number;
  memory_mb: number;
  disk_total_bytes: number;
  disks: VmDisk[];
  boot_slot?: string | null;
  /** Phase 31: customer-safe mounted-ISO handle (opaque id + display
   * name) served by the backend — no storage/volume reconstruction
   * happens client-side. Null/absent when no ISO is mounted. */
  mounted_iso?: { id: string; name: string } | null;
  max_extra_disks?: number;
  cpu_usage_fraction: number;
  memory_usage_bytes: number;
  memory_source?: "guest" | "host";
  memory_total_bytes?: number;
  memory_available_bytes?: number;
  swap_usage_bytes?: number;
  swap_total_bytes?: number;
  memory_includes_cache?: boolean;
  uptime_seconds: number;
  qmpstatus: string;
  ostype: string | null;
  tags: string;
  ip_addresses: GuestInterface[];
  ip_status: "ok" | "agent_disabled" | "agent_not_running" | "vm_stopped" | "unreachable";
  snapshot_count: number;
}

export interface SnapshotSummary {
  name: string;
  description: string;
  snaptime?: number;
  vmstate?: boolean;
  parent?: string;
}

/** rrddata window for the disk-activity card (Phase 35). */
export type DiskMetricTimeframe = "hour" | "day" | "week";

/**
 * One point of the disk-activity time series. Mirrors the backend's
 * customerDiskMetricPoint — STRICT allowlist of five keys, null where PVE
 * had no sample (gap) or a negative sentinel.
 */
export interface DiskMetricPoint {
  t: number;
  read_bps: number | null;
  write_bps: number | null;
  io_stall_some_pct: number | null;
  io_stall_full_pct: number | null;
}

/** GET /api/{vms,containers}/:vmid/metrics/disk response. interval_s is
 * null when fewer than two points were returned. */
export interface DiskMetricsResponse {
  timeframe: DiskMetricTimeframe;
  interval_s: number | null;
  points: DiskMetricPoint[];
}

export interface BackupRowSummary {
  id: string;
  ctime: number;
  size: number;
  notes?: string;
  protected?: boolean;
  verificationState?: string;
}

/** The customer's PVE cluster backup job summary. hour/minute are null
 * (with rawSchedule set) when the PVE schedule isn't a plain daily HH:MM
 * the portal's picker can represent — the UI shows the raw string and
 * disables schedule editing then. */
export interface BackupCardJob {
  hour: number | null;
  minute: number | null;
  rawSchedule?: string;
  serverCount: number;
  enabled: boolean;
}

export interface BackupCardState {
  storageConfigured: boolean;
  job: BackupCardJob | null;
  member: boolean;
  activeRun: {
    startedAt: string;
    status: string;
    trigger: "manual" | "schedule";
  } | null;
  backups: BackupRowSummary[];
  lastBackupAt: number | null;
  vmMissing?: true;
}

export type VmAction = "start" | "shutdown" | "stop";

export function statusColor(s: string): string {
  switch (s) {
    case "running":
      return "green";
    case "stopped":
      return "gray";
    case "paused":
      return "yellow";
    case "orphaned":
      return "red";
    case "creating":
      return "blue";
    // Apps status machine (additive — app_instances rows share StatusPill):
    case "pending":
      return "blue";
    case "degraded":
      return "orange";
    case "deleting":
      return "gray";
    case "failed":
      return "red";
    case "updating":
      return "yellow";
    default:
      return "blue";
  }
}

export function formatBytes(bytes: number): string {
  if (!bytes || bytes <= 0) return "—";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v >= 10 || i === 0 ? Math.round(v) : v.toFixed(1)} ${units[i]}`;
}

export function formatRam(mb: number): string {
  if (mb >= 1024 && mb % 1024 === 0) return `${mb / 1024} GB`;
  if (mb >= 1024) return `${(mb / 1024).toFixed(1)} GB`;
  return `${mb} MB`;
}

export function formatUptime(seconds: number): string {
  if (!seconds || seconds <= 0) return "—";
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

export interface VmActionResult {
  ok: boolean;
  message: string;
}

/**
 * Fire a power op and return a customer-facing result tuple. PVE failures
 * arrive as `{error:'pve_unavailable'}` (502) from the backend; everything
 * else is surfaced via the friendlyError mapper.
 */
export async function vmAction(
  vmid: number,
  action: VmAction,
  body?: { force?: boolean; timeout?: number }
): Promise<VmActionResult> {
  const verb = action === "start" ? "starting" : "stopping";
  try {
    await apiPost<{ status: string }>(`/api/vms/${vmid}/${action}`, body);
    return { ok: true, message: `VM ${verb}…` };
  } catch (err) {
    const apiErr = err as ApiError;
    if (apiErr.status === 502 && apiErr.error === "pve_unavailable") {
      return { ok: false, message: "Proxmox is unavailable. Please try again." };
    }
    if (apiErr.status === 403) {
      return { ok: false, message: "You don't have access to this VM." };
    }
    return { ok: false, message: friendlyError(apiErr) };
  }
}
